import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Claim } from '@wsphere/warranties/domain';
import { ReplaySubject, combineLatest, from, map, of, switchMap } from 'rxjs';
import { ClaimService } from '../../services';

interface Share {
  payee: string;
  kind: string;
  amount: number;
  /** Of the net payable, rounded. */
  percent: number;
  /** The row behind a current share; null before the first split and in history. */
  allocation: Claim.Allocation.ListAllocationsItem | null;
}

// Same labels as the payments table.
const PAYMENT_BADGES: Partial<Record<Claim.ClaimPayment.Status, { text: string; theme: ThemeColor }>> = {
  [Claim.ClaimPayment.Status.DRAFT]: { text: 'Draft', theme: 'default' },
  [Claim.ClaimPayment.Status.ISSUED]: { text: 'Issued', theme: 'info' },
  [Claim.ClaimPayment.Status.COMPLETED]: { text: 'Completed', theme: 'success' },
};
const NOT_PREPARED = { text: 'Not prepared', theme: 'default' as ThemeColor };

interface SplitVersion {
  version: number;
  shares: Share[];
  created: Date;
  createdBy: string | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-allocations',
  templateUrl: './claim-allocations.component.html',
  standalone: false,
})
export class ClaimAllocationsComponent {
  constructor(private claimSvc: ClaimService) {}

  @PropertyListener('claim') claim$ = new ReplaySubject<Claim.Model | null>(1);
  @Input() claim: Claim.Model | null = null;

  /** The active authorization whose split this shows: the first one or a supplemental. */
  @PropertyListener('authorizationId') authorizationId$ = new ReplaySubject<string | null>(1);
  @Input() authorizationId: string | null = null;

  /** Staff with clm_ManageAllocations for this claim's distributor. */
  @Input() canManage = false;

  /** An approval holding back the authorization; the server refuses a new split while there is one. */
  @Input() hold: Claim.Override.Model | null = null;

  protected readonly heldBackMessage = Claim.Override.heldBackMessage;

  /** Emits after a split is saved, so the page reloads. */
  @Output() changed = new EventEmitter<void>();

  isHistoryShown = false;

  // Reloaded whenever the page refreshes the claim.
  readonly view$ = combineLatest([this.claim$, this.authorizationId$]).pipe(
    switchMap(([claim, authorizationId]) => {
      const authorization = Claim.Authorization.getActiveAuthorizations(claim?.authorizations ?? []).find(
        ({ id }) => id === authorizationId
      );
      if (!claim || !authorization) return of(null);

      return combineLatest([of(claim), of(authorization), from(this.claimSvc.listAllocations(claim.id))]);
    }),
    map(data => (data ? this.toView(...data) : null))
  );

  paymentBadge(status: Claim.ClaimPayment.Status | null) {
    return status ? (PAYMENT_BADGES[status] ?? null) : NOT_PREPARED;
  }

  formatAmount(amount: number, currency = 'USD') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);
  }

  private toView(
    claim: Claim.Model,
    authorization: Claim.Authorization.Model,
    { items }: Claim.Allocation.ListAllocationsResponse
  ) {
    const own = items.filter(allocation => allocation.authorizationId === authorization.id);
    const current = own.filter(allocation => allocation.status === Claim.Allocation.Status.ACTIVE);

    const versions = [...new Set(own.map(allocation => allocation.version))].sort((a, b) => b - a);
    const toVersion = (version: number): SplitVersion => {
      const rows = own.filter(allocation => allocation.version === version);
      const isCurrent = rows[0].status === Claim.Allocation.Status.ACTIVE;
      return {
        version,
        shares: rows.map(row => this.toShare(row, authorization.netPayable, isCurrent ? row : null)),
        created: rows[0].created,
        createdBy: rows[0].createdBy?.displayName || rows[0].createdBy?.email || null,
      };
    };

    return {
      authorization,
      current,
      currentVersion: current.length ? toVersion(current[0].version) : null,
      // Before the first split, all of it goes to the payee picked when authorizing.
      authorizedShare: authorization.payeeType
        ? this.toShare(
            { ...authorization, payeeType: authorization.payeeType, amount: authorization.netPayable },
            authorization.netPayable,
            null
          )
        : null,
      history: versions.filter(version => version !== current[0]?.version).map(toVersion),
      // The server refuses a new split while a payment of this authorization is prepared.
      isLocked:
        (claim.paymentBalances?.find(balance => balance.authorizationId === authorization.id)?.reserved ?? 0) > 0,
    };
  }

  private toShare(
    allocation: { payeeType: Claim.Authorization.PayeeType; payeeName: string | null; amount: number },
    netPayable: number,
    current: Claim.Allocation.ListAllocationsItem | null
  ): Share {
    const isShop = allocation.payeeType === Claim.Authorization.PayeeType.SERVICE_PROVIDER;
    return {
      payee: allocation.payeeName ?? (isShop ? 'Repair shop' : 'Customer'),
      kind: isShop ? 'Repair shop' : 'Customer',
      amount: allocation.amount,
      percent: netPayable ? Math.round((allocation.amount / netPayable) * 100) : 0,
      allocation: current,
    };
  }
}
